// Firebase anonymous authentication. Sign in on load; hand a fresh ID token to every
// backend call (the backend's @require_anonymous_auth verifies it).
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, type Auth } from 'firebase/auth';
import {
  initializeAppCheck,
  getToken as getAppCheckTokenInternal,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
import { FIREBASE_CONFIG, RECAPTCHA_SITE_KEY } from './config';

let auth: Auth | null = null;
let appCheck: AppCheck | null = null;

export async function initAuth(): Promise<void> {
  const app = initializeApp(FIREBASE_CONFIG);

  // Firebase App Check (additive, non-blocking). Attaches an invisible reCAPTCHA Enterprise
  // attestation to backend requests via getAppCheckToken(). Skipped entirely when no site
  // key is configured so local builds without the key still run.
  if (RECAPTCHA_SITE_KEY) {
    // Dev/local: hand localhost/CI a debug token instead of failing attestation. Mirrors the
    // import.meta.env.DEV branch used elsewhere (config.ts/view.ts). When VITE_APPCHECK_DEBUG_TOKEN
    // is supplied (chat/.env.local), use that exact registered token; otherwise `true` makes the
    // SDK mint+log a fresh one to register. This whole branch is tree-shaken from prod builds.
    if (import.meta.env.DEV) {
      const debugToken = (import.meta.env.VITE_APPCHECK_DEBUG_TOKEN as string | undefined)?.trim();
      (
        self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }
      ).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken || true;
    }
    try {
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });
      // Prime the first attestation token before the app makes any request. initializeAppCheck
      // returns synchronously and mints the token asynchronously in the background, so without
      // this the very first request (the page-load /config call) races ahead and goes out with
      // no X-Firebase-AppCheck header (~33% of sessions in production). Force a real mint here
      // and wait for it, but bound the wait: a slow or blocked reCAPTCHA must never wedge page
      // load. On timeout/failure we proceed; the backend is in monitoring mode and the request
      // still goes out header-less. Once primed, the SDK caches the token for later requests.
      await primeAppCheckToken(appCheck);
    } catch (err) {
      // Never let App Check init break the app; the backend is in monitoring mode.
      console.warn('App Check initialization failed; continuing without attestation.', err);
      appCheck = null;
    }
  } else {
    console.warn(
      'VITE_RECAPTCHA_SITE_KEY is not set; Firebase App Check is disabled for this build.',
    );
  }

  auth = getAuth(app);
  await signInAnonymously(auth);
  await waitForUser();
}

/** Max time to wait for the very first App Check token to be minted before proceeding. */
const APPCHECK_PRIME_TIMEOUT_MS = 4000;

/**
 * Mint the first App Check token (or give up after a bounded wait). Forces a real mint so the
 * token is in the SDK cache before the first request. Resolves regardless of outcome: a slow or
 * blocked reCAPTCHA must not hang startup, and a failure must not break the app (the backend is
 * in monitoring mode, so the request simply goes out without the header).
 */
async function primeAppCheckToken(ac: AppCheck): Promise<void> {
  try {
    await Promise.race([
      getAppCheckTokenInternal(ac, false),
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('App Check token priming timed out')),
          APPCHECK_PRIME_TIMEOUT_MS,
        ),
      ),
    ]);
  } catch (err) {
    console.warn('App Check token priming failed; first request may omit the header.', err);
  }
}

/** Max time to wait for a single App Check token attempt before treating it as failed. */
const APPCHECK_FETCH_TIMEOUT_MS = 3000;

/**
 * One bounded App Check token attempt. Resolves to the token string on success, or null if the
 * fetch throws OR exceeds APPCHECK_FETCH_TIMEOUT_MS OR returns an empty token. Never rejects, so a
 * thrown/slow reCAPTCHA can't escape and can't hang the request indefinitely. `forceRefresh`
 * selects cached-vs-fresh mint behaviour (false = cheap cached/auto-refreshed; true = force a fresh
 * mint, which recovers expired/half-refreshed/transiently-failed tokens).
 */
async function tryGetAppCheckToken(ac: AppCheck, forceRefresh: boolean): Promise<string | null> {
  try {
    const result = await Promise.race([
      getAppCheckTokenInternal(ac, forceRefresh),
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('App Check token fetch timed out')),
          APPCHECK_FETCH_TIMEOUT_MS,
        ),
      ),
    ]);
    return result.token || null;
  } catch (err) {
    console.warn(
      `App Check token fetch failed (forceRefresh=${forceRefresh}); will fall back.`,
      err,
    );
    return null;
  }
}

/**
 * A Firebase App Check token, or null if App Check is disabled or both attempts fail. Callers
 * attach it as the `X-Firebase-AppCheck` header when present.
 *
 * Robustness: the cheap cached read (getToken(false)) occasionally returns nothing on mobile —
 * an expired/half-refreshed token, or a reCAPTCHA execute that transiently failed — which is the
 * likely cause of the intermittent mid-session misses seen in production. So on any falsy/empty
 * token or thrown error we do exactly ONE force-refresh retry (getToken(true)) that mints a fresh
 * token. Each attempt is bounded by APPCHECK_FETCH_TIMEOUT_MS, so worst case is two attempts
 * (~6s) — consistent with the existing 4s prime philosophy and never an indefinite hang.
 *
 * Graceful degradation is preserved exactly: if App Check is disabled (appCheck null) we return
 * null immediately, and if both attempts genuinely fail we still return null and let the request
 * proceed header-less (the backend is in monitor mode; the client must never hard-block).
 */
export async function getAppCheckToken(): Promise<string | null> {
  if (!appCheck) return null;

  // Attempt 1: cheap cached/auto-refreshed token.
  const cached = await tryGetAppCheckToken(appCheck, false);
  if (cached) return cached;

  // Attempt 2: force a fresh mint to recover from expired/transiently-failed tokens.
  const refreshed = await tryGetAppCheckToken(appCheck, true);
  if (refreshed) return refreshed;

  // Both attempts failed; proceed header-less (monitor mode tolerates this).
  console.warn('App Check token unavailable after retry; proceeding without the header.');
  return null;
}

function waitForUser(timeoutMs = 10000): Promise<void> {
  if (auth?.currentUser) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const unsub = onAuthStateChanged(auth!, (user) => {
      if (user) {
        unsub();
        resolve();
      }
    });
    setTimeout(() => {
      unsub();
      reject(new Error('Anonymous sign-in timed out'));
    }, timeoutMs);
  });
}

/** A valid Firebase ID token (refreshed by the SDK as needed). */
export async function getToken(): Promise<string> {
  if (!auth) throw new Error('Auth not initialized');
  if (!auth.currentUser) await waitForUser();
  return auth.currentUser!.getIdToken();
}
